/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { Suspense, useCallback, useEffect, useState } from "react";

import { css, keyframes } from "@compiled/react";
// eslint-disable-next-line no-restricted-imports
import { Global } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import ChevronLeftLargeIcon from "@atlaskit/icon/glyph/chevron-left-large";
import ChevronRightLargeIcon from "@atlaskit/icon/glyph/chevron-right-large";
import CrossIcon from "@atlaskit/icon/glyph/cross";
import MenuIcon from "@atlaskit/icon/glyph/menu";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import FeatureGates from "@atlassian/feature-gate-js-client";
import UFOSegment from "@atlassian/wac-ufo-segment";
import AdminTrialCart from "@atlassiansox/admin-trial-cart";

import { Initializer } from "../../../analytics/atl-analytics/initializer";
import DropdownLayout from "../../../components/global-nav/dropdown-layout/src";
import { Search } from "../../../components/global-nav/search/src";
import { GlobalNavUserProfile } from "../../../components/global-nav/user-profile-new/src";
import WplButton from "../../../components/wpl/button/src";
import { Logo, ProductKeyTypes } from "../../../libraries/wpl/foundations/src";
import { useLoggedInUser } from "../../../utils/logged-in-user-provider";

import { ProductsMain, ProductsSideBar } from "./products-dropdown";
import { ResourcesMain, ResourcesSideBar } from "./resources-dropdown";
import { SolutionsMain, SolutionsSideBar } from "./solutions-dropdown";

const WacAppSwitcher = React.lazy(() =>
    // without the line ts-ignored below TSC throws this error:
    // "Relative import paths need explicit file extensions in EcmaScript imports when '--moduleResolution' is 'node16' or 'nodenext'. Did you mean '../../../components/global-nav/app-switcher/src/index.js'?"
    // the file of course ends in .tsx, when you try to add that there is a different error:
    // "An import path can only end with a '.tsx' extension when 'allowImportingTsExtensions' is enabled."

    // @ts-ignore
    import("../../../components/global-nav/app-switcher/src/index").then(
        (module) => ({
            default: module.WacAppSwitcher
        })
    )
);

type DropdownData = {
    key: string;
    title: string;
    content: JSX.Element | string;
};

const globalCss = css({
    body: {
        position: "relative"
    }
});

const globalMenuOpenCss = css({
    body: {
        height: "100vh",
        overflow: "hidden"
    }
});

const navigationBarWrapperCss = css({
    alignItems: "flex-start",
    background: "#ffffff",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    position: "relative",
    zIndex: 5,
    [media.below.md]: {
        ".showMenu &": {
            background: "#f4f5f7",
            height: "100vh",
            overflow: "auto",
            position: "fixed",
            insetBlockStart: 0,
            width: "100%"
        }
    }
});

const navigationBarCss = css({
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "row",
    padding: `0 ${token("space.200")}`,
    [media.above.md]: {
        height: "72px",
        maxWidth: "1200px"
    },
    [media.below.md]: {
        height: "56px",
        position: "relative",
        zIndex: 1
    }
});

const logoWrapperCss = css({
    display: "flex",
    alignItems: "center",
    [media.below.md]: {
        width: "100%"
    }
});

const logoLinkWrapperCss = css({
    "display": "flex",
    "> svg": {
        height: "auto",
        width: "193px",
        [media.below.md]: {
            "transition": "opacity 250ms",
            "width": "161px",
            ".categoryActive &": {
                opacity: 0
            }
        },
        [media.above.md]: {
            marginInlineEnd: "12px"
        }
    }
});

const mobileTitleWrapperCss = css({
    display: "none",
    width: "70%",
    [media.below.md]: {
        "alignItems": "center",
        "display": "flex",
        "height": "100%",
        "opacity": 0,
        "pointerEvents": "none",
        "position": "absolute",
        "insetBlockStart": 0,
        "transform": "translate3d(30%, 0, 0)",
        "transition": "transform 250ms, opacity 250ms",
        ".categoryActive &": {
            opacity: 1,
            pointerEvents: "auto",
            transform: "translate3d(0, 0, 0) !important"
        },
        "> a": {
            alignItems: "center",
            color: "#42526e",
            display: "flex",
            fontSize: "1.125rem",
            fontWeight: 400,
            textDecoration: "none"
        }
    }
});

const categoriesWrapperCss = css({
    "display": "flex",
    "flex": 1,
    "flexDirection": "row",
    "listStyle": "none",
    "margin": 0,
    "padding": 0,
    "> li > a": {
        alignItems: "center",
        color: "#42526e",
        display: "flex",
        textDecoration: "none"
    },
    [media.above.md]: {
        "marginInlineStart": token("space.100"),
        "> li > a": {
            "borderRadius": "3px",
            "padding": token("space.025"),
            "paddingInlineStart": token("space.150"),
            "textDecoration": "none",
            "marginInlineEnd": "2px",
            "&:hover, &:focus": {
                backgroundColor: "#dfe1e5"
            }
        }
    },
    [media.below.md]: {
        "background": "#ffffff",
        "display": "none",
        "flexDirection": "column",
        "marginBlockStart": "0.5px",
        "transform": "translate3d(0, 0, 0)",
        "transition": "transform 250ms",
        "> li > a": {
            "borderBlockEnd": "0.5px solid #c1c7d0",
            "fontSize": "1.125rem",
            "fontWeight": 400,
            "lineHeight": "48px",
            "padding": `0 ${token("space.200")}`,
            ":hover": {
                backgroundColor: "#dfe1e5"
            }
        },
        ".showMenu &": {
            display: "flex",
            insetInlineStart: 0,
            position: "absolute",
            insetBlockStart: "200%",
            width: "100%"
        },
        ".categoryActive &": {
            transform: "translate3d(-100%, 0, 0)"
        }
    }
});

const categoryArrowDownCss = css({
    display: "block",
    color: "#0052cc",
    height: "24px",
    [media.below.md]: {
        display: "none"
    }
});

const categoryArrowRightCss = css({
    display: "none",
    [media.below.md]: {
        color: "#42526e",
        display: "flex",
        paddingInlineStart: "18px",
        position: "relative",
        insetBlockStart: "2px",
        svg: {
            width: "14px"
        }
    }
});

const dropdownSlideDownAnimation = keyframes({
    from: {
        maxHeight: "0px"
    },
    to: {
        maxHeight: "2000px"
    }
});

const dropdownSlideUpAnimation = keyframes({
    from: {
        maxHeight: "2000px"
    },
    to: {
        maxHeight: "0px"
    }
});

const searchWrapperCss = css({
    display: "flex",
    [media.below.md]: {
        ".showMenu &": {
            display: "none"
        }
    }
});

const actionsWrapperCss = css({
    "alignItems": "center",
    "display": "flex",
    "flexDirection": "row",
    "listStyle": "none",
    "margin": 0,
    "padding": 0,
    [media.below.md]: {
        "borderBlockStart": "0.5px solid #c1c7d0",
        "display": "none",
        ".showMenu &": {
            background: "#ffffff",
            display: "flex",
            height: "100%",
            insetInlineStart: 0,
            position: "absolute",
            insetBlockStart: "100%",
            width: "100%"
        },
        // "Try Now" button on left, others on right
        [`& li:first-of-type`]: {
            flex: 1,
            paddingInlineStart: token("space.200")
        },
        "& li:last-of-type": {
            paddingInlineEnd: token("space.200")
        }
    },
    "> li": {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        paddingInlineStart: "12px"
    }
});

const menuButtonCss = css({
    display: "flex",
    justifyContent: "center",
    paddingInlineStart: "1rem"
});

const tryNowCss = css({
    a: {
        fontWeight: 500
    }
});

const switcherDividerCss = css({
    borderInlineStart: "1px solid #dfe1e5",
    width: "1px",
    height: "24px",
    marginInlineEnd: token("space.100"),
    [media.below.md]: {
        display: "none"
    }
});

const cartButtonCss = css({
    "paddingInlineStart": "0 !important",
    "> div": {
        marginInlineStart: "20px",
        height: "30px",
        insetBlockStart: "8px",
        position: "relative"
    },
    "> div:empty": {
        margin: 0,
        height: 0,
        insetBlockStart: 0
    }
});

const mobileMenuToggleCss = css({
    display: "none",
    [media.below.md]: {
        display: "block",
        svg: {
            height: "48px",
            width: "48px"
        }
    }
});

const closeMenuCss = css({
    [media.below.md]: {
        paddingInlineEnd: "8px",
        position: "relative",
        zIndex: 1,
        svg: {
            color: "#293141",
            height: "32px",
            width: "32px"
        }
    }
});

const pageBlurCss = css({
    "display": "none",
    ".showPageBlur &": {
        background: "rgba(216, 216, 216, 0.6)",
        insetBlockEnd: 0,
        content: '" "',
        display: "block",
        insetInlineStart: 0,
        position: "absolute",
        insetBlockStart: 0,
        width: "100%",
        zIndex: 1
    }
});

// eslint-disable-next-line complexity
const GlobalNav = (props: {
    showTryNow: boolean;
    color?: string;
    productKey: ProductKeyTypes;
    isHideNavigationDropdowns?: boolean;
}) => {
    const intl = useIntl();
    const navigationDropdowns: DropdownData[] = [
        {
            key: "products",
            title: intl.formatMessage({
                id: "wacGlobalNavigation.category.products",
                defaultMessage: "Products"
            }),
            content: (
                <DropdownLayout
                    main={<ProductsMain productKey={props.productKey} />}
                    sidebar={<ProductsSideBar productKey={props.productKey} />}
                />
            )
        },
        {
            key: "solutions",
            title: intl.formatMessage({
                id: "wacGlobalNavigation.category.solutions",
                defaultMessage: "Solutions"
            }),
            content: (
                <DropdownLayout
                    main={<SolutionsMain productKey={props.productKey} />}
                    sidebar={<SolutionsSideBar productKey={props.productKey} />}
                />
            )
        },
        {
            key: "resources",
            title: intl.formatMessage({
                id: "wacGlobalNavigation.category.resources",
                defaultMessage: "Resources"
            }),
            content: (
                <DropdownLayout
                    main={<ResourcesMain productKey={props.productKey} />}
                    sidebar={<ResourcesSideBar productKey={props.productKey} />}
                />
            )
        }
    ];

    const [showMenu, setShowMenu] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<null | DropdownData>(
        null
    );
    const [activePageBlur, setActivePageBlur] = useState(false);
    const [activeDownAnimation, setActiveDownAnimation] = useState(true);
    const [activeUpAnimation, setActiveUpAnimation] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    useEffect(() => {
        Initializer.autoTrack();
    }, [showMenu, activeDropdown, isSearchOpen]);

    function dismissMenu() {
        setActiveUpAnimation(true);
        setActiveDownAnimation(false);
        setTimeout(() => {
            setActivePageBlur(false);
            setShowMenu(false);
            setActiveDropdown(null);
        }, 300);
    }

    const escFunction = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === "Escape" && (showMenu || isSearchOpen)) {
                dismissMenu();
                setIsSearchOpen(false);
            }
        },
        [isSearchOpen, showMenu]
    );

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    /* TODO: Implement setIsUserLoggedIn, user logged in status (in useEffect), once GWP-51 in implemented  */
    const { isUserLoggedIn } = useLoggedInUser();

    const navigationWrapperClassName = `${
        activeDropdown ? "categoryActive" : ""
    } ${showMenu ? "showMenu" : ""} ${activePageBlur ? "showPageBlur" : ""}`;

    const toggleDropdown = (dropdownData: DropdownData) => {
        setIsSearchOpen(false);
        if (activeDropdown && activeDropdown.key === dropdownData.key) {
            dismissMenu();
            return;
        } else if (activeDropdown) {
            setActiveDownAnimation(false);
            setActiveUpAnimation(false);
            setActiveDropdown(dropdownData);
            return;
        }
        setActivePageBlur(true);
        setActiveDownAnimation(true);
        setActiveUpAnimation(false);
        setShowMenu(true);
        setActiveDropdown(dropdownData);
    };

    // This is an A/A test - we don't actually do anything differently based on cohort, we just want the exposure
    FeatureGates.getExperiment(
        // https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/wac-uip-global-nav-try-now-aa-test/setup
        "wac-uip-global-nav-try-now-aa-test"
    );

    FeatureGates.getExperiment(
        //  https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/growth_aa_educational/setup
        "growth_aa_educational"
    );

    const dropdownWrapperCss = [
        !activeUpAnimation &&
            css({
                overflow: "hidden",
                position: "absolute",
                background: "#ffffff",
                width: "100%",
                [media.above.md]: {
                    borderBlockStart: "1px solid #ebecf0",
                    boxShadow: "0 10px 5px rgba(0, 0, 0, 0.15)",
                    insetInlineStart: 0,
                    insetBlockStart: "100%"
                },
                [media.below.md]: {
                    insetInlineStart: "100%",
                    padding: `0 ${token("space.200")}`,
                    position: "absolute",
                    insetBlockStart: 0
                }
            }),
        !activeDownAnimation &&
            css({
                overflow: "hidden",
                position: "absolute",
                background: "#ffffff",
                width: "100%",
                [media.above.md]: {
                    borderBlockStart: "1px solid #ebecf0",
                    boxShadow: "0 10px 5px rgba(0, 0, 0, 0.15)",
                    insetInlineStart: 0,
                    insetBlockStart: "100%"
                },
                [media.below.md]: {
                    insetInlineStart: "100%",
                    padding: `0 ${token("space.200")}`,
                    position: "absolute",
                    insetBlockStart: 0
                }
            }),
        activeUpAnimation &&
            css({
                [media.above.md]: {
                    animation: `${dropdownSlideUpAnimation} 300ms ease`,
                    zIndex: 12
                },
                overflow: "hidden",
                position: "absolute",
                background: "#ffffff",
                width: "100%",
                [media.above.md]: {
                    borderBlockStart: "1px solid #ebecf0",
                    boxShadow: "0 10px 5px rgba(0, 0, 0, 0.15)",
                    insetInlineStart: 0,
                    insetBlockStart: "100%"
                },
                [media.below.md]: {
                    insetInlineStart: "100%",
                    padding: `0 ${token("space.200")}`,
                    position: "absolute",
                    insetBlockStart: 0
                }
            }),
        activeDownAnimation &&
            css({
                [media.above.md]: {
                    animation: `${dropdownSlideDownAnimation} 500ms ease`,
                    zIndex: 12
                },
                overflow: "hidden",
                position: "absolute",
                background: "#ffffff",
                width: "100%",
                [media.above.md]: {
                    borderBlockStart: "1px solid #ebecf0",
                    boxShadow: "0 10px 5px rgba(0, 0, 0, 0.15)",
                    insetInlineStart: 0,
                    insetBlockStart: "100%"
                },
                [media.below.md]: {
                    insetInlineStart: "100%",
                    padding: `0 ${token("space.200")}`,
                    position: "absolute",
                    insetBlockStart: 0
                }
            })
    ];

    return (
        <UFOSegment name="global-nav">
            <div className={navigationWrapperClassName}>
                {/* @ts-ignore */}
                <Global styles={globalCss} />
                {/* @ts-ignore */}
                {showMenu && <Global styles={globalMenuOpenCss} />}
                <div
                    css={navigationBarWrapperCss}
                    style={{
                        backgroundColor:
                            !showMenu && props.color ? props.color : "#ffffff"
                    }}
                >
                    <div css={navigationBarCss}>
                        <div css={logoWrapperCss}>
                            <a
                                css={logoLinkWrapperCss}
                                href={useLocalizedHref("/")}
                                aria-label={intl.formatMessage({
                                    id: "wacGlobalNavigation.logo-label",
                                    defaultMessage: "Atlassian logo"
                                })}
                                data-event="clicked"
                                data-event-component="linkButton"
                                data-event-container="globalNavWac"
                                data-product-key="jiraSoftware"
                            >
                                <Logo
                                    brand="atlassian"
                                    type="logo"
                                    appearance="brand"
                                />
                            </a>
                        </div>
                        <div css={mobileTitleWrapperCss}>
                            <a
                                href="#"
                                aria-label={intl.formatMessage({
                                    id: "wacGlobalNavigation.mobile-title-return",
                                    defaultMessage: "Return to categories menu"
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveDropdown(null);
                                }}
                            >
                                <ChevronLeftLargeIcon label="" />{" "}
                                {activeDropdown && activeDropdown.title}
                            </a>
                        </div>
                        <ul css={categoriesWrapperCss}>
                            {!props.isHideNavigationDropdowns &&
                                navigationDropdowns.map((dropdownData) => (
                                    <li key={dropdownData.key}>
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleDropdown(dropdownData);
                                            }}
                                            data-event="clicked"
                                            data-event-component="linkButton"
                                            data-event-container="headerDropdownLink"
                                            data-product-key="jiraSoftware"
                                        >
                                            {dropdownData.title}
                                            <div css={categoryArrowDownCss}>
                                                <ChevronDownIcon label="" />
                                            </div>
                                            <div css={categoryArrowRightCss}>
                                                <ChevronRightLargeIcon label="" />
                                            </div>
                                        </a>
                                        {activeDropdown &&
                                            dropdownData.key ===
                                                activeDropdown.key && (
                                                <div css={dropdownWrapperCss}>
                                                    {activeDropdown.content}
                                                </div>
                                            )}
                                    </li>
                                ))}
                        </ul>
                        <div css={searchWrapperCss}>
                            <div>
                                <Search
                                    isOpen={isSearchOpen}
                                    openSearch={() => {
                                        setIsSearchOpen(true);
                                        dismissMenu();
                                    }}
                                    closeSearch={() => setIsSearchOpen(false)}
                                />
                            </div>
                        </div>
                        <ul css={actionsWrapperCss}>
                            <li css={tryNowCss}>
                                {props.showTryNow && (
                                    <WplButton
                                        text={intl.formatMessage({
                                            id: "wacGlobalNavigation.try-now-button-label",
                                            defaultMessage: "Try Now"
                                        })}
                                        // eslint-disable-next-line react-hooks/rules-of-hooks
                                        url={useLocalizedHref("/try")}
                                        size="small"
                                        dataEventProps={{
                                            "data-product-key":
                                                props.productKey,
                                            "data-event": "clicked",
                                            "data-event-component":
                                                "linkButton",
                                            "data-event-container": "navbar"
                                        }}
                                    />
                                )}
                            </li>
                            <li>
                                <div css={switcherDividerCss} />
                                {isUserLoggedIn && (
                                    <Suspense>
                                        <WacAppSwitcher
                                            allowJoinSitesProvider={true}
                                            appSwitcherLabel={intl.formatMessage(
                                                {
                                                    id: "wacGlobalNavigation.app-switcher-my-products",
                                                    defaultMessage:
                                                        "My products"
                                                }
                                            )}
                                        />
                                    </Suspense>
                                )}
                            </li>
                            <li>
                                <GlobalNavUserProfile />
                            </li>
                            <li css={cartButtonCss}>
                                <AdminTrialCart version={0} fields={{}} />
                            </li>
                        </ul>
                        <div css={mobileMenuToggleCss}>
                            <a
                                href="#"
                                aria-label={intl.formatMessage({
                                    id: "wacGlobalNavigation.menu-icon.toggle",
                                    defaultMessage: "Toggle Menu"
                                })}
                                css={menuButtonCss}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (showMenu) {
                                        setActiveDropdown(null);
                                    }
                                    setShowMenu(!showMenu);
                                    setIsSearchOpen(false);
                                }}
                            >
                                {showMenu ? (
                                    <span css={closeMenuCss}>
                                        <CrossIcon
                                            label={intl.formatMessage({
                                                id: "wacGlobalNavigation.menu-icon.close",
                                                defaultMessage: "Close Menu"
                                            })}
                                        />
                                    </span>
                                ) : (
                                    <MenuIcon
                                        primaryColor="#64686f"
                                        label={intl.formatMessage({
                                            id: "wacGlobalNavigation.menu-icon.open",
                                            defaultMessage: "Open Menu"
                                        })}
                                    />
                                )}
                            </a>
                        </div>
                    </div>
                </div>
                <button
                    aria-hidden="true"
                    css={pageBlurCss}
                    onClick={dismissMenu}
                />
            </div>
        </UFOSegment>
    );
};

GlobalNav.displayName = "GlobalNav";

export default GlobalNav;
